import { X } from 'lucide-react';
import type { ComponentProps } from 'react';

import type { VsImageProps } from '@vs-shop/headless/useVsImage';
import Image from '@vs-shop/image-link/Image';
import { Dialog, DialogClose, DialogOverlay, DialogPopup, DialogPortal } from '@vs-shop/ui/dialog';
import { clsxm } from '@vs-shop/utils';

import { RenderedPart, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * The gallery's lightbox: the same section with the screen to itself.
 *
 * The parts below are what the overlay adds — the surface it hangs on, the way
 * out of it, its two titles, and the column its media centres in. Everything
 * else is `productGallery.tsx` painting its `OVERLAY` appearance: the same
 * stage, the same strip, the same thumbs and the same step controls. A medium
 * that is a video is framed by `productGalleryVideo.tsx`, which serves the
 * page's stage and this one alike.
 *
 *   <ProductGalleryLightbox open={open} onOpenChange={…}>
 *     <ProductGalleryLightboxPanel>
 *       <ProductGalleryLightboxClose aria-label={closeLabel} />
 *
 *       <ProductGalleryLightboxMain>
 *         <ProductGalleryLightboxTitle className="lap:hidden">{name}</ProductGalleryLightboxTitle>
 *         <Carousel setApi={setApi} opts={{ loop: true }} disableKeyboardNavigation>
 *           <ProductGalleryPrevControl variant={OVERLAY} aria-label={prevLabel} onClick={…} />
 *           <ProductGalleryNextControl variant={OVERLAY} aria-label={nextLabel} onClick={…} />
 *           <ProductGalleryLightboxViewport>
 *             <ProductGalleryStripContent variant={OVERLAY}>
 *               {media.map((item, idx) => (
 *                 <ProductGalleryStripItem key={idx}>…</ProductGalleryStripItem>
 *               ))}
 *             </ProductGalleryStripContent>
 *           </ProductGalleryLightboxViewport>
 *         </Carousel>
 *       </ProductGalleryLightboxMain>
 *
 *       <ProductGalleryThumbBar variant={OVERLAY}>
 *         <ProductGalleryLightboxThumbTitle className="hidden lap:block">{name}</…>
 *         <ProductGalleryThumbGrid variant={OVERLAY}>
 *           <ProductGalleryThumb variant={OVERLAY} aria-current={…}>…</ProductGalleryThumb>
 *         </ProductGalleryThumbGrid>
 *       </ProductGalleryThumbBar>
 *     </ProductGalleryLightboxPanel>
 *   </ProductGalleryLightbox>
 *
 * **The overlay's behaviour is the primitive's.** Focus trapping, focus restore
 * on close, `Escape`, scroll locking and the backdrop's dismiss are
 * `@vs-shop/ui`'s dialog, which is `@base-ui-components/react` underneath. A
 * hand-rolled overlay here would re-solve every one of them, and would get one
 * of them wrong.
 *
 * **`'use client'` appears nowhere in this file.** The interactive leaf is the
 * dialog primitive itself; these parts are plain components that render it, so a
 * tenant's own client composition holds the open state and the handlers ride the
 * native prop spread — the shape the rest of this section already has.
 *
 * The two titles are neutral elements. A lightbox title that entered the page's
 * outline would compete with the product's own heading, on a page whose subject
 * is the product; a page that wants one anyway passes it as `render`.
 */

export type ProductGalleryLightboxProps = ComponentProps<typeof Dialog>;

/**
 * The overlay itself: open state in, dismissal out. Renders no box of its own —
 * what a shopper sees is {@link ProductGalleryLightboxPanel}.
 */
export const ProductGalleryLightbox = (props: ProductGalleryLightboxProps) => <Dialog {...props} />;

/**
 * The surface the lightbox fills, and the dimmed page behind it.
 *
 * The backdrop is this part's own rather than a composition's: it is what makes
 * the panel read as being over the page rather than in it, and it carries the
 * click that dismisses — a composition that forgot it would render a panel that
 * looks mounted and cannot be left by clicking away.
 *
 * The panel takes the whole viewport on a phone and stands off its edges above
 * `lap`, where a shopper can still see the page it belongs to.
 */
const productGalleryLightboxPanelClasses =
  'bg-background fixed inset-0 z-(--z-max) flex flex-col justify-between ' +
  'lap:inset-10 lap:flex-row overflow-y-auto rounded-lg outline-none';

/**
 * The dim layer. Not a part: nothing is composed into it, and it exists at all
 * only because the panel does.
 *
 * `transition-none` puts back what the primitive's own recipe assumes — a
 * dialog that fades its scrim in. This one appears with its imagery, and a
 * scrim that arrives late reads as a lag on the photograph.
 */
const productGalleryLightboxBackdropClasses = 'transition-none';

export type ProductGalleryLightboxPanelProps = ComponentProps<typeof DialogPopup>;

/** The lightbox's surface, portalled over the page it dims. */
export const ProductGalleryLightboxPanel = ({
  className,
  ...props
}: ProductGalleryLightboxPanelProps) => (
  <DialogPortal>
    <DialogOverlay className={productGalleryLightboxBackdropClasses} />
    <DialogPopup
      data-slot="product-gallery-lightbox-panel"
      className={clsxm(productGalleryLightboxPanelClasses, className)}
      {...props}
    />
  </DialogPortal>
);

/**
 * The way out, in the panel's trailing corner above everything the panel holds.
 * Its mark is the part's own, so the control cannot be composed without one;
 * what it is called is the tenant's, through `aria-label`.
 */
const productGalleryLightboxCloseClasses = 'lap:top-6 lap:right-6 absolute top-4 right-4 z-10';

/** The mark inside the control. Internal anatomy: no composition renders it. */
const productGalleryLightboxCloseIconClasses = 'size-6';

export type ProductGalleryLightboxCloseProps = ComponentProps<typeof DialogClose>;

/** The control that closes the lightbox. */
export const ProductGalleryLightboxClose = ({
  className,
  children,
  ...props
}: ProductGalleryLightboxCloseProps) => (
  <DialogClose
    data-slot="product-gallery-lightbox-close"
    className={clsxm(productGalleryLightboxCloseClasses, className)}
    {...props}
  >
    <X
      data-slot="product-gallery-lightbox-close-icon"
      className={productGalleryLightboxCloseIconClasses}
    />
    {children}
  </DialogClose>
);

/**
 * The column the imagery occupies: everything the panel has left once the thumb
 * column has taken its share. Above `lap` it centres what it holds; below it,
 * it clears the close control and stacks from the top.
 */
const productGalleryLightboxMainClasses =
  'lap:mt-0 lap:items-center lap:justify-center mt-12 flex h-full w-full flex-1 flex-col overflow-hidden';

export type ProductGalleryLightboxMainProps = ComponentProps<'div'>;

/** The imagery column of the panel. */
export const ProductGalleryLightboxMain = ({
  className,
  ...props
}: ProductGalleryLightboxMainProps) => (
  <div
    data-slot="product-gallery-lightbox-main"
    className={clsxm(productGalleryLightboxMainClasses, className)}
    {...props}
  />
);

/**
 * Where the media centres inside the imagery column, two elements deep: the box
 * that takes the column's height, and the inset box the media is bounded by.
 * The inner one is the part's own anatomy — a media set of differing shapes
 * centres against one box, and a composition supplying its own would size the
 * next photograph against a different one.
 */
const productGalleryLightboxViewportClasses =
  'flex h-full max-h-full w-full flex-col items-center justify-center';

const productGalleryLightboxViewportInnerClasses =
  'my-10 flex h-full max-h-full w-full max-w-full flex-col items-center justify-center';

export type ProductGalleryLightboxViewportProps = ComponentProps<'div'>;

/** The box the lightbox's media centres in. */
export const ProductGalleryLightboxViewport = ({
  className,
  children,
  ...props
}: ProductGalleryLightboxViewportProps) => (
  <div
    data-slot="product-gallery-lightbox-viewport"
    className={clsxm(productGalleryLightboxViewportClasses, className)}
    {...props}
  >
    <div
      data-slot="product-gallery-lightbox-viewport-inner"
      className={productGalleryLightboxViewportInnerClasses}
    >
      {children}
    </div>
  </div>
);

/**
 * The product's name over the imagery, where the thumb column is not beside it
 * to carry the name itself.
 */
const productGalleryLightboxTitleClasses = 'mx-4 mb-4 flex-shrink-0 text-xl';

export type ProductGalleryLightboxTitleProps = ComponentProps<'div'> & {
  /**
   * The element to paint instead of the neutral `<div>`. The one place a page
   * decides whether the lightbox's name is a heading, and at what level.
   */
  readonly render?: RenderElement;
};

/** The name over the imagery. */
export const ProductGalleryLightboxTitle = ({
  className,
  render,
  ...props
}: ProductGalleryLightboxTitleProps) => (
  <RenderedPart
    dataSlot="product-gallery-lightbox-title"
    className={clsxm(productGalleryLightboxTitleClasses, className)}
    render={render}
    {...props}
  />
);

/**
 * The product's name at the head of the thumb column — the same name, read as a
 * label over a list rather than as a caption over a photograph, which is why it
 * is smaller, heavier and its own part rather than an appearance of the one
 * above.
 */
const productGalleryLightboxThumbTitleClasses =
  'font-body lap:w-4/5 text-lg/6 font-semibold tracking-tight';

export type ProductGalleryLightboxThumbTitleProps = ComponentProps<'span'>;

/**
 * The name heading the thumb column.
 *
 * No `render` escape, unlike the title over the imagery: this one labels a list
 * inside a surface that already has a title, and a page wanting the lightbox's
 * name in its outline wants it once.
 */
export const ProductGalleryLightboxThumbTitle = ({
  className,
  ...props
}: ProductGalleryLightboxThumbTitleProps) => (
  <span
    data-slot="product-gallery-lightbox-thumb-title"
    className={clsxm(productGalleryLightboxThumbTitleClasses, className)}
    {...props}
  />
);
